import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Wallet, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  X,
  Filter,
  Image as ImageIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTopUpRequests() {
  const [requests, setRequests] = useState([]);
  const [customerRequests, setCustomerRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [activeTab, setActiveTab] = useState("agen"); // "agen" or "pelanggan"
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionNote, setActionNote] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchCustomerRequests();
    markNotificationsAsRead();
  }, [filter]);

  const markNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.log("Error marking notifications as read:", err);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/topup-requests?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
    } catch (err) {
      // Do NOT auto-clear tokens or redirect on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data agen (unauthorized).");
      } else {
        toast.error("Gagal memuat data agen");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerRequests = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/customer-topup-requests?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerRequests(response.data.requests || []);
    } catch (err) {
      console.log("Error fetching customer requests:", err);
    }
  };

  const handleAction = async (action) => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem("admin_token");
      const isCustomer = selectedRequest.customer_id !== undefined;
      const endpoint = isCustomer
        ? `${API}/admin/customer-topup-requests/${selectedRequest.id}/action`
        : `${API}/admin/topup-requests/${selectedRequest.id}/action`;
      
      await axios.post(
        endpoint,
        { action, note: actionNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(action === "approve" ? "Top-up disetujui!" : "Top-up ditolak");
      setSelectedRequest(null);
      setActionNote("");
      fetchRequests();
      fetchCustomerRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memproses");
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const pendingCount = requests.filter(r => r.status === "pending").length;
  const customerPendingCount = customerRequests.filter(r => r.status === "pending").length;
  const currentRequests = activeTab === "agen" ? requests : customerRequests;

  return (
    <AdminLayout title="Permintaan Top-Up">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Tab Selection: Agen/Petugas vs Pelanggan */}
        <div className="flex gap-2 mb-4 border-b">
          <button
            onClick={() => setActiveTab("agen")}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "agen"
                ? "border-sky-500 text-sky-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-agen"
          >
            Agen / Petugas
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("pelanggan")}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "pelanggan"
                ? "border-sky-500 text-sky-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-pelanggan"
          >
            Pelanggan (Google)
            {customerPendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full">
                {customerPendingCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { value: "pending", label: "Menunggu", icon: Clock, color: "amber" },
            { value: "approved", label: "Disetujui", icon: CheckCircle, color: "emerald" },
            { value: "rejected", label: "Ditolak", icon: XCircle, color: "red" },
            { value: "all", label: "Semua", icon: Filter, color: "slate" }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filter === tab.value
                  ? `bg-${tab.color}-100 text-${tab.color}-700`
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`filter-${tab.value}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.value === "pending" && pendingCount > 0 && filter !== "pending" && (
                <span className="ml-1 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : currentRequests.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Tidak Ada Permintaan
              </h3>
              <p className="text-slate-600">
                Belum ada permintaan top-up {activeTab === "pelanggan" ? "dari pelanggan" : "dari agen/petugas"} {filter !== "all" && `dengan status "${filter}"`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            <AnimatePresence>
              {currentRequests.map((req) => (
                <motion.div
                  key={req.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            req.status === "pending" ? "bg-amber-100" :
                            req.status === "approved" ? "bg-emerald-100" : "bg-red-100"
                          }`}>
                            <Wallet className={`w-6 h-6 ${
                              req.status === "pending" ? "text-amber-600" :
                              req.status === "approved" ? "text-emerald-600" : "text-red-600"
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800">
                                {req.customer_name || req.user_name}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                req.user_role === "pelanggan"
                                  ? "bg-purple-100 text-purple-700"
                                  : req.user_role === "agen" 
                                  ? "bg-emerald-100 text-emerald-700" 
                                  : "bg-sky-100 text-sky-700"
                              }`}>
                                {req.user_role}
                              </span>
                            </div>
                            {req.customer_email && (
                              <p className="text-xs text-slate-500">{req.customer_email}</p>
                            )}
                            <p className="text-2xl font-bold text-slate-800 mt-1">
                              {formatCurrency(req.amount)}
                            </p>
                            {req.transfer_amount && (
                              <p className="text-sm font-medium text-sky-600">
                                Transfer: {formatCurrency(req.transfer_amount)} 
                                <span className="text-slate-400 ml-1">(+{req.unique_code})</span>
                              </p>
                            )}
                            <p className="text-sm text-slate-500">
                              {formatDate(req.created_at)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                            req.status === "pending" ? "bg-amber-100 text-amber-700" :
                            req.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                            "bg-red-100 text-red-700"
                          }`}>
                            {req.status === "pending" ? "Menunggu" :
                             req.status === "approved" ? "Disetujui" : "Ditolak"}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedRequest(req)}
                            className="gap-1"
                            data-testid={`view-btn-${req.id}`}
                          >
                            <Eye className="w-4 h-4" />
                            Lihat
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Detail Modal */}
        <AnimatePresence>
          {selectedRequest && (
            <div 
              className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedRequest(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative max-w-lg w-full bg-white rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="sticky top-0 p-4 border-b border-slate-200 flex items-center justify-between bg-white z-10">
                  <h3 className="font-semibold text-slate-800">Detail Permintaan Top-Up</h3>
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>

                <div className="p-5 space-y-5">
                  {/* User Info */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Nama User</p>
                        <p className="font-semibold text-lg">{selectedRequest.user_name}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedRequest.user_role === "agen" 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-sky-100 text-sky-700"
                      }`}>
                        {selectedRequest.user_role}
                      </span>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="bg-sky-50 rounded-xl p-4 text-center">
                    <p className="text-sm text-sky-600 mb-1">Nominal Top-Up</p>
                    <p className="text-3xl font-bold text-sky-700">
                      {formatCurrency(selectedRequest.amount)}
                    </p>
                  </div>

                  {/* Payment Proof */}
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Bukti Transfer
                    </p>
                    {selectedRequest.payment_proof ? (
                      <img
                        src={selectedRequest.payment_proof}
                        alt="Bukti Transfer"
                        className="w-full rounded-xl border border-slate-200"
                      />
                    ) : (
                      <div className="w-full h-48 bg-slate-100 rounded-xl flex items-center justify-center">
                        <p className="text-slate-400">Tidak ada bukti</p>
                      </div>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-slate-500 text-xs mb-1">Tanggal Pengajuan</p>
                      <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
                    </div>
                    {selectedRequest.processed_at && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-slate-500 text-xs mb-1">Tanggal Diproses</p>
                        <p className="font-medium">{formatDate(selectedRequest.processed_at)}</p>
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="text-center">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                      selectedRequest.status === "pending" ? "bg-amber-100 text-amber-700" :
                      selectedRequest.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {selectedRequest.status === "pending" && <Clock className="w-4 h-4" />}
                      {selectedRequest.status === "approved" && <CheckCircle className="w-4 h-4" />}
                      {selectedRequest.status === "rejected" && <XCircle className="w-4 h-4" />}
                      {selectedRequest.status === "pending" ? "Menunggu Persetujuan" :
                       selectedRequest.status === "approved" ? "Disetujui" : "Ditolak"}
                    </span>
                  </div>

                  {/* Note (if processed) */}
                  {selectedRequest.note && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm text-slate-500 mb-1">Catatan Admin</p>
                      <p className="text-slate-700">{selectedRequest.note}</p>
                    </div>
                  )}

                  {/* Action Buttons (only for pending) */}
                  {selectedRequest.status === "pending" && (
                    <div className="space-y-4 pt-2">
                      <div>
                        <label className="block text-sm font-medium text-slate-600 mb-2">
                          Catatan (opsional)
                        </label>
                        <Textarea
                          value={actionNote}
                          onChange={(e) => setActionNote(e.target.value)}
                          placeholder="Tambahkan catatan..."
                          rows={2}
                          data-testid="action-note-input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleAction("reject")}
                          disabled={processing}
                          variant="outline"
                          className="flex-1 h-12 border-red-200 text-red-600 hover:bg-red-50"
                          data-testid="reject-btn"
                        >
                          <XCircle className="w-5 h-5 mr-2" />
                          Tolak
                        </Button>
                        <Button
                          onClick={() => handleAction("approve")}
                          disabled={processing}
                          className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
                          data-testid="approve-btn"
                        >
                          {processing ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5 mr-2" />
                              Setujui
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </AdminLayout>
  );
}
